import { useState } from 'react';
import { Package, Plus, Truck, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useCancelPurchaseOrder, usePurchaseOrders } from '@/api/hooks/usePurchaseOrders';
import type { Medicine, PurchaseOrder, PurchaseOrderStatus } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { NewPurchaseOrderDialog } from '@/features/purchasing/NewPurchaseOrderDialog';
import type { ReorderPrefill } from '@/features/purchasing/NewPurchaseOrderDialog';
import { ReceivePurchaseOrderDialog } from '@/features/purchasing/ReceivePurchaseOrderDialog';
import { ReorderSuggestions } from '@/features/purchasing/ReorderSuggestions';
import { formatDate, money } from '@/lib/utils';

const STATUS_VARIANT: Record<PurchaseOrderStatus, 'ok' | 'out' | 'low' | 'secondary'> = {
  Received: 'ok',
  Cancelled: 'out',
  Ordered: 'low',
  Draft: 'secondary',
};

export function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const { data, isLoading } = usePurchaseOrders();
  const cancelPO = useCancelPurchaseOrder();

  const [createOpen, setCreateOpen] = useState(false);
  const [prefill, setPrefill] = useState<ReorderPrefill | undefined>(undefined);
  const [receiveTarget, setReceiveTarget] = useState<PurchaseOrder | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrder | null>(null);

  function openBlankPO() {
    setPrefill(undefined);
    setCreateOpen(true);
  }

  function handleReorder(medicine: Medicine, suggestedQty: number) {
    setPrefill({
      id: medicine.id,
      name: medicine.name,
      costPrice: medicine.costPrice,
      supplierId: medicine.supplierId,
      suggestedQty,
    });
    setCreateOpen(true);
  }

  async function handleCancel() {
    if (!cancelTarget) return;
    try {
      await cancelPO.mutateAsync(cancelTarget.id);
      toast({ title: 'Purchase order cancelled', variant: 'success' });
      setCancelTarget(null);
    } catch (error) {
      toast({
        title: 'Could not cancel',
        description: error instanceof ApiError ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  const orders = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">{t('purchaseOrdersTitle')}</h1>
          <p className="text-sm text-muted-foreground">{data ? `${data.total} order(s)` : 'Loading…'}</p>
        </div>
        <Can permission={PERMISSIONS.purchasesCreate}>
          <Button onClick={openBlankPO}>
            <Plus /> New PO
          </Button>
        </Can>
      </div>

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="reorder">Reorder suggestions</TabsTrigger>
        </TabsList>

        <TabsContent value="reorder">
          <ReorderSuggestions onOrder={handleReorder} />
        </TabsContent>

        <TabsContent value="orders" className="space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
                <Truck className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No purchase orders yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {orders.map((po) => (
                <Card key={po.id} className="flex flex-wrap items-center gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold">{po.poNumber}</span>
                      <Badge variant={STATUS_VARIANT[po.status]}>{po.status}</Badge>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {po.supplierName ?? 'Unknown supplier'} · {po.items.length} item(s) · {formatDate(po.createdAt)}
                    </p>
                  </div>

                  <span className="tabular shrink-0 text-sm font-semibold">{money(po.totalCost)}</span>

                  <div className="flex shrink-0 items-center gap-2">
                    {po.status === 'Ordered' && (
                      <Can permission={PERMISSIONS.purchasesReceive}>
                        <Button variant="outline" size="sm" onClick={() => setReceiveTarget(po)}>
                          <Package /> Receive
                        </Button>
                      </Can>
                    )}
                    {po.status === 'Ordered' && (
                      <Can permission={PERMISSIONS.purchasesDelete}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-status-expired hover:bg-status-expired/10 hover:text-status-expired"
                          onClick={() => setCancelTarget(po)}
                        >
                          <X /> Cancel
                        </Button>
                      </Can>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <NewPurchaseOrderDialog open={createOpen} onOpenChange={setCreateOpen} prefillMedicine={prefill} />

      <ReceivePurchaseOrderDialog
        po={receiveTarget}
        open={receiveTarget !== null}
        onOpenChange={(open) => !open && setReceiveTarget(null)}
      />

      <ConfirmDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title={`Cancel ${cancelTarget?.poNumber ?? 'this order'}?`}
        description="This can't be undone. A fully received PO can't be cancelled."
        confirmLabel="Cancel order"
        pending={cancelPO.isPending}
        onConfirm={handleCancel}
      />
    </div>
  );
}
